import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';
import { MEDICATIONS_ERROR_STATE_PREFIX } from '../../components/layout/treatment-plan/medications/medicationsConstants';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IMedication } from '../../utils/types';

const name = 'medications';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: These legacy thunk payloads are untyped at their API boundary.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<MedicationsState>) =>
    createExtraReducers(builder),
});

export interface MedicationsState {
  treatmentPlanId: number | null;
  list: Array<IMedication>;
  error: any;
  loading: boolean;
  creatingMedication: boolean;
  errorState: any;
}

type MedicationsStateUpdate = {
  [Key in keyof MedicationsState]: { key: Key; data: MedicationsState[Key] };
}[keyof MedicationsState];

function createInitialState(): MedicationsState {
  return {
    treatmentPlanId: null,
    list: [],
    loading: false,
    error: null,
    creatingMedication: false,
    errorState: {},
  };
}

function createReducers() {
  return {
    clearState,
    addUnsavedMedication,
    updateUnsavedMedication,
    removeUnsavedMedication,
    updateErrorState,
    updateState,
  };

  function clearState(state: MedicationsState) {
    state.treatmentPlanId = null;
    state.list = [];
    state.error = null;
    state.loading = false;
    state.creatingMedication = false;
  }

  function updateState(state: MedicationsState, action: PayloadAction<MedicationsStateUpdate>) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }

  function addUnsavedMedication(state: MedicationsState) {
    state.list = [
      ...state.list,
      { date_start: '', medication_name: '', dosage: '', physician: '', range: 0 },
    ];
  }

  function updateUnsavedMedication(state: MedicationsState, action: any) {
    const { index, data } = action.payload;
    state.list = state.list.map((medication, i) => (i === index ? data : medication));
  }

  function removeUnsavedMedication(state: MedicationsState, action: any) {
    const { index } = action.payload;
    const newErrorState = { ...state.errorState };
    delete newErrorState[`${MEDICATIONS_ERROR_STATE_PREFIX}-${'new-' + index}`];
    state.list = state.list.filter((medication, i) => i !== index);
    state.errorState = newErrorState;
  }

  function updateErrorState(state: MedicationsState, action: any) {
    state.errorState = { ...state.errorState, ...action.payload };
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getList: getList(),
    getMedication: getMedication(),
    updateMedication: updateMedication(),
    createMedication: createMedication(),
    deleteMedication: deleteMedication(),
  };

  function getList() {
    return createAsyncThunk(`${name}/getList`, async ({ getAccessTokenSilently, planId }: any) => {
      const response = await fetchWrapper.get({
        url: `${baseUrl}/${planId}/medications/`,
        getAccessTokenSilently,
      });
      return response;
    });
  }

  function getMedication() {
    return createAsyncThunk(
      `${name}/getMedication`,
      async ({ getAccessTokenSilently, planId, medicationId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${planId}/medications/${medicationId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function deleteMedication() {
    return createAsyncThunk(
      `${name}/deleteMedication`,
      async ({ getAccessTokenSilently, planId, medicationId }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${planId}/medications/${medicationId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function updateMedication() {
    return createAsyncThunk(
      `${name}/updateMedication`,
      async ({ data, getAccessTokenSilently, planId, medicationId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/medications/${medicationId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function createMedication() {
    return createAsyncThunk(
      `${name}/createMedication`,
      async ({ data, getAccessTokenSilently, planId }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/${planId}/medications/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<MedicationsState>) {
  const { createMedication, getList, updateMedication, deleteMedication } = extraActions;

  builder
    // createMedication()
    .addCase(createMedication.pending, (state: MedicationsState) => {
      state.loading = true;
      state.creatingMedication = true;
      state.error = null;
    })
    .addCase(createMedication.fulfilled, (state: MedicationsState, action) => {
      state.loading = false;
      state.list = state.list.map((medication, i) =>
        i === action.meta.arg.index ? action.payload : medication,
      );
      state.creatingMedication = false;
    })
    .addCase(createMedication.rejected, (state: MedicationsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      state.creatingMedication = false;
      toast.error(state.error.message);
    })
    // getList()
    .addCase(getList.pending, (state: MedicationsState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(getList.fulfilled, (state: MedicationsState, action: PayloadAction<IMedication[]>) => {
      const unsavedMedications = state.list.filter((medication: IMedication) => {
        return !medication.id;
      });
      state.loading = false;
      state.list = action.payload ? [...action.payload, ...unsavedMedications] : unsavedMedications;
      state.creatingMedication = false;
    })
    .addCase(getList.rejected, (state: MedicationsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      state.creatingMedication = false;
      toast.error(state.error.message);
    })
    // updateMedication()
    .addCase(updateMedication.pending, (state: MedicationsState) => {
      state.error = null;
    })
    .addCase(updateMedication.rejected, (state: MedicationsState, action: any) => {
      state.error = action.error;
      toast.error(state.error.message);
    })
    // deleteMedication()
    .addCase(deleteMedication.pending, (state: MedicationsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deleteMedication.fulfilled,
      (
        state: MedicationsState,
        action: PayloadAction<IMedication, string, { arg: { medicationId: number } }>,
      ) => {
        const newErrorState = { ...state.errorState };
        delete newErrorState[`${MEDICATIONS_ERROR_STATE_PREFIX}-${action.meta.arg.medicationId}`];
        state.list = state.list.filter(
          (medication) => medication.id !== action.meta.arg.medicationId,
        );
        state.loading = false;
        state.errorState = newErrorState;
      },
    )
    .addCase(deleteMedication.rejected, (state: MedicationsState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const medicationsActions = { ...slice.actions, ...extraActions };
export const medicationsReducer = slice.reducer;
